<template>
  <div
    :class="inputClasses"
    class="app-input"
  >
    <label
      v-if="label"
      :class="isFloating"
      :for="id || name"
    >
      {{ label }}
    </label>

    <!-- Icon -->
    <span
      v-if="$slots.icon"
      class="app-input__icon"
    >
      <slot name="icon"/>
    </span>
    <!-- Input Field -->
    <masked-input
      type="tel"
      name="date_of_birth"
      class="app-input__field"
      :class="fieldClasses"
      :value="value"
      :mask="mask"
      :guide="true"
      placeholder="__ / __ / ____"
      pattern="\d*"
      placeholder-char="_"
      @input="updateValue($event)"
      @blur.native="checkValue()"
      @blur="focused = false"
      @focus="focused = true"
    />

    <div
      v-if="hint"
      class="app-input__hint"
    >
      {{ hint }}
    </div>

    <div
      v-if="error"
      class="app-input__error"
    >
      <small class="app-input__error-message">
        {{ error }}
      </small>
    </div>
  </div>
</template>

<script>
import MaskedInput from 'vue-text-mask'

export default {
  name: 'app-input',

  // Components
  components: {
    MaskedInput
  },

  props: {
    autocomplete: String,
    id: String,
    label: String,
    value: [String, Number],
    placeholder: String,
    horizontal: Boolean,
    error: String,
    hint: String,
    type: {
      type: String,
      default: 'text'
    },
    name: {
      type: String,
      default: 'app-input__field'
    }
  },

  data () {
    return {
      focused: false,
      blurred: false,
      mask: [/\d/, /\d/, ' ', '/', ' ', /\d/, /\d/, ' ', '/', ' ', /\d/, /\d/, /\d/, /\d/]
    }
  },

  computed: {
    isFloating () {
      if (!this.value && !this.focused) return false
      return 'floating'
    },

    inputClasses () {
      return {
        'active': this.isFloating,
        'horizontal': this.horizontal,
        'focused': this.focused,
        'has-error': this.error,
        'has-label': this.label
      }
    },

    fieldClasses () {
      return {
        'has-error': this.error,
        'has-icon': this.icon,
        'has-label': this.label
      }
    }
  },

  // Methods
  methods: {
    // Update the input value
    updateValue (value) {
      this.$emit('input', value) // Emit 'input' on change
      if (value && !value.includes('_')) {
        // The input is complete, emit the 'filled' event
        this.$emit('filled')
      }
    },

    // On blur, reset the input if it's incomplete
    checkValue () {
      if (this.value) {
        setTimeout(() => {
          if (this.value.includes('_')) {
            // Input is incomplete, emit 'reset' event
            // this.value = undefined
            this.$emit('reset', undefined)
          } else {
            // The input is complete, emit the 'filled' event
            this.$emit('filled')
          }
        }, 0)
      }
    }
  }
}
</script>

<style scoped lang="scss">
$input-color-label: #A7A7A7;
$input-color-background: #F4F4F4;
$input-color-text: #3F3F3F;
$input-color-border: #D6D6D6;
$input-color-divider: #DDD;
$input-color-border-active: #3CBBF5;
$input-color-error: #DF2C21;

.app-input {
  position: relative;
  // margin: auto;
  display: flex;
  flex-wrap: wrap;
  min-width: 150px;
  width: 100%;
  margin-bottom: 20px;

  @include breakpoint('tablet-landscape-up') {
    margin-left: 10px;

    &:first-child {
      margin-left: 0;
    }
  }

  ::placeholder {
    color: transparent;
  }

  &.active ::placeholder {
    color: $input-color-text;
  }

  label {
    position: absolute;
    top: 0;
    left: 0;
    font-size: 16px;
    font-weight: 400;
    color: $input-color-label;
    pointer-events: none;
    transform-origin: 0 0;
    transform: translate3D(15px, 15px, 0);
    transition: transform 175ms ease-out;

    @include breakpoint('tablet-portrait-up') {
      font-size: 22px;
      transform: translate3D(15px, 26px, 0);
      left: 15px;
    }

    &.floating {
      transform: translate3D(15px, 5px, 0) scale(0.75);

      @include breakpoint('tablet-portrait-up') {
        transform: translate3D(15px, 12px, 0) scale(0.63);
      }
    }
  }

  &__field {
    height: 55px;
    color: $input-color-text;
    background-color: $input-color-background;
    box-shadow: none;
    border: 1px solid transparent;
    border-top: 1px solid $input-color-border;
    flex-basis: 100%;
    outline: none;
    padding: 10px 0 10px 15px;
    font-size: 18px;
    caret-color: $brand-color-primary;
    letter-spacing: .04em;
    border-radius: 4px;
    transition: outline 150ms ease-out,
      box-shadow 150ms ease-out,
      border-color 150ms ease-out;

    @include breakpoint('tablet-portrait-up') {
      border-width: 2px;
      font-size: 22px;
      height: 75px;
      padding-left: 28px;
    }

    &.has-icon {
      padding-left: 40px;
    }

    &:focus {
      border-color: $input-color-border-active;
    }

    &.has-label {
      padding-top: 25px;
    }

    &.has-error {
      border-color: $input-color-error;
      box-shadow: 0 4px 0 0 $input-color-error;
    }
  }

  &__error {
    position: absolute;
    bottom: -23px;
    right: 0px;

    @include breakpoint('tablet-portrait-up') {
      font-size: 14px;
      bottom: auto;
      right: auto;
      top: -28px;
      left: 0;
    }
  }

  &__error-message {
    color: $input-color-error;
    font-size: 12px;
    font-style: italic;

    @include breakpoint('tablet-portrait-up') {
      font-size: 14px;
    }
  }

  &__hint {
    position: absolute;
    bottom: -25px;
    left: 0;
    font-size: 9px;
    color: #090909;
    opacity: 0.5;

    @include breakpoint('tablet-portrait-up') {
      font-size: 16px;
    }

    @include breakpoint('desktop-up') {
      font-size: 14px;
    }
  }

  &__icon {
    position: absolute;
    left: 15px;
    font-size: 10px;
    top: 16px;
  }
}
</style>
